import { ScoresResponse } from 'api/events/eventApiTypes';
import { authenticatedFetch, handleContentDisposition } from 'api/fetch';
import {
  LearnerPurchaseRequest,
  TeamResponse,
  MutationResponse,
  type PaginatedResponse,
  type PathParamsBase,
} from 'api/httpEntities';
import { cleanObject } from 'api/utils';
import { Dialects, ProfileDialectStrategies } from 'types/dialects';
import { ProfileType } from 'types/types';
import { STARTS_WITH, type SearchMode, QueryParamsBase } from 'api/search';
import {
  PaginatedTeamLearnerParams,
  PaginatedTeamLearnersResponse,
  CreateMultiOrgTeamRequest,
  GetProfileIDsBodyType,
  MultiOrgTeamEditRequest,
  ProfileIds,
  ScoreCount,
  SingleOrgTeamEditRequest,
  TeamColourCountsByOrganisationResponse,
  TeamLearnerData,
  WheelPosition,
  WheelPositionByOrganisation,
  WheelPositionSummaryByOrganisationResponse,
  WheelPositionSummaryResponse,
} from 'api';
import type {
  AvailableLearnersResponse,
  GetAvailableLearnersRequest,
} from './teamApiTypes';

function transformStructure(wheelPositions: WheelPosition[]) {
  return wheelPositions.reduce(
    (
      prev: { wheelPosition: WheelPosition; totalScoreCount: number },
      wheel: WheelPosition,
    ) => {
      const resultObj = prev;
      const key = Object.keys(wheel)[0];
      resultObj.wheelPosition[key] = wheel[key];
      resultObj.totalScoreCount += wheel[key].scoreCount.conscious;
      return resultObj;
    },
    { wheelPosition: {}, totalScoreCount: 0 },
  );
}

export function transformTeamWheelByOrganisationStructure(
  activeOrganisations: Record<string, boolean> = {},
  wheelPositions: WheelPositionByOrganisation[] = [],
) {
  return wheelPositions.reduce(
    (
      prev: { wheelPositions: WheelPosition; totalScoreCount: number },
      wheel: WheelPositionByOrganisation,
    ) => {
      const resultObj = prev;
      const key = Object.keys(wheel)[0];
      resultObj.wheelPositions[key] = {
        scoreCount: {
          conscious: 0,
          lessConscious: 0,
        },
      };

      wheel[key].scoreCounts.forEach(
        ({ organisationId, conscious, lessConscious }) => {
          if (activeOrganisations[organisationId]) {
            resultObj.wheelPositions[key].scoreCount.conscious += conscious;
            resultObj.wheelPositions[key].scoreCount.lessConscious +=
              lessConscious;
          }
        },
      );

      resultObj.totalScoreCount +=
        resultObj.wheelPositions[key].scoreCount.conscious;
      return resultObj;
    },
    { wheelPositions: {}, totalScoreCount: 0 },
  );
}

export interface TeamPageParams extends QueryParamsBase {
  organisationId?: string;
  searchMode?: SearchMode;
  createdBy?: string;
  includeTotal?: boolean;
}
export interface TeamPathParams extends PathParamsBase {
  organisationId?: string;
}

/**
 * API:
 * - [Get Window List Teams](https://engineering.insights.com/reference/generated/core-api.html#window-list-teams)
 */
export const getPaginatedTeams = async (
  {
    searchTerm,
    searchMode,
    organisationId,
    createdBy,
    includeTotal = false,
    ...rest
  }: TeamPageParams = {
    searchTerm: '',
    searchMode: STARTS_WITH,
  },
): Promise<PaginatedResponse<TeamResponse>> => {
  const queries = new URLSearchParams(
    cleanObject({
      ...(organisationId ? { organisationId } : {}),
      ...(searchTerm ? { searchTerm, searchMode } : {}),
      ...(createdBy ? { createdBy } : {}),
      ...(includeTotal ? { includeTotal } : {}),
      ...rest,
    }),
  );
  const url = '/api/v1/teams';

  const { json } = await authenticatedFetch<
    Promise<PaginatedResponse<TeamResponse>>
  >(queries.size > 0 ? `${url}?${queries.toString()}` : url);

  return await json();
};

export const getTeam = async (teamId: string): Promise<TeamResponse> => {
  const { json } = await authenticatedFetch<TeamResponse>(
    `/api/v1/teams/${teamId}`,
  );

  return json();
};

type DownloadCsvResponse = {
  blob: Blob;
  filename: string;
};

export const downloadTeamCsv = async (
  teamId: string,
): Promise<DownloadCsvResponse> => {
  const res = await authenticatedFetch<DownloadCsvResponse>(
    `/api/v1/teams/${teamId}/wheel`,
    {
      method: 'Get',
      headers: { Accept: 'text/csv' },
    },
  );
  const data = handleContentDisposition(res);

  return data;
};

export const downloadEventCsv = async (
  eventId: string,
): Promise<DownloadCsvResponse> => {
  const res = await authenticatedFetch<DownloadCsvResponse>(
    `/api/v1/events/${eventId}/wheel`,
    {
      method: 'Get',
      headers: { Accept: 'text/csv' },
    },
  );
  const data = handleContentDisposition(res);

  return data;
};

export const updateMultiOrgTeam = async ({
  teamId,
  formData,
}: MultiOrgTeamEditRequest): Promise<MutationResponse> => {
  const url = `/api/v1/teams/${teamId}`;
  const { json } = await authenticatedFetch<MutationResponse>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  });

  return json();
};

export const updateSingleOrgTeam = async ({
  teamId,
  organisationId,
  formData,
}: SingleOrgTeamEditRequest): Promise<MutationResponse> => {
  const url = `/api/v1/organisations/${organisationId}/teams/${teamId}`;
  const { json } = await authenticatedFetch<MutationResponse>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  });

  return json();
};

export const getTeamLearners = async (
  queryParams?: PaginatedTeamLearnerParams,
  pathParams?: { teamId: string | number },
): Promise<PaginatedTeamLearnersResponse> => {
  const { teamId } = pathParams ?? {};
  const {
    searchTerm = '',
    purchaseStatus = 'ALL',
    query = '',
  } = queryParams ?? {};
  const params = new URLSearchParams({
    purchaseStatus,
    ...(searchTerm ? { searchTerm } : {}),
    ...(query ? { query } : {}),
  });
  const url = `/api/v1/teams/${teamId}/learners?${params.toString()}`;

  const { json } = await authenticatedFetch<PaginatedTeamLearnersResponse>(
    url,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  return json();
};

export const addTeamLearner = async ({
  teamId,
  learnerId,
}: TeamLearnerData): Promise<void> => {
  const url = `/api/v1/teams/${teamId}/learners/${learnerId}`;
  const { json } = await authenticatedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  await json();
};

export const removeTeamLearner = async ({
  teamId,
  learnerId,
}: TeamLearnerData): Promise<void> => {
  const url = `/api/v1/teams/${teamId}/learners/${learnerId}`;
  const { json } = await authenticatedFetch(url, { method: 'DELETE' });
  await json();
};

export const getTeamWheelScores = async (
  teamId: string,
): Promise<ScoresResponse> => {
  const url = `/api/v1/teams/${teamId}/wheel`;
  const { json } = await authenticatedFetch<ScoresResponse>(url);

  return json();
};

export type SummaryResponse = {
  wheelPositions: Record<number, ScoreCount>;
  totalScoreCount: number;
};
export const getTeamScoreSummary = async (
  teamId: string,
): Promise<SummaryResponse> => {
  const url = `/api/v1/teams/${teamId}/wheel/summary`;
  const { json } = await authenticatedFetch<WheelPositionSummaryResponse>(url);
  const data = await json();
  const { wheelPosition, totalScoreCount } = transformStructure(
    data.wheelPositions,
  );
  return { wheelPositions: wheelPosition, totalScoreCount };
};

export const getTeamScoreSummaryByOrganisation = async (
  teamId: string,
): Promise<WheelPositionSummaryByOrganisationResponse> => {
  const url = `/api/v1/teams/${teamId}/wheel/summary-by-organisation`;
  const { json } =
    await authenticatedFetch<WheelPositionSummaryByOrganisationResponse>(url);
  return json();
};

export const getTeamColourCountsByOrganisation = async (
  teamId: string,
): Promise<TeamColourCountsByOrganisationResponse> => {
  const url = `/api/v1/teams/${teamId}/wheel/colour-counts-by-organisation`;
  const { json } =
    await authenticatedFetch<TeamColourCountsByOrganisationResponse>(url);
  return json();
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  const url = `/api/v1/teams/${teamId}`;
  const { json } = await authenticatedFetch(url, { method: 'DELETE' });

  await json();
};

export const purchaseProfilesForTeam = async ({
  teamId,
  request,
}: {
  teamId: string;
  request: LearnerPurchaseRequest;
}) => {
  const { success } = await authenticatedFetch(
    `/api/v1/teams/${teamId}/profiles`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );

  return success;
};

export const addAllLearnersToTeam = async (teamId: string) => {
  const url = `/api/v1/teams/${teamId}/learners/all`;
  const { json } = await authenticatedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  await json();
};

export const removeAllLearnersToTeam = async (teamId: string) => {
  const url = `/api/v1/teams/${teamId}/learners`;
  const { json } = await authenticatedFetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  await json();
};

export const createMultiOrgTeam = async (body: CreateMultiOrgTeamRequest) => {
  const { json } = await authenticatedFetch<TeamResponse>('/api/v1/teams', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return json();
};

export const getProfileIds = async (
  teamId: string,
  profileType: ProfileType,
  body: GetProfileIDsBodyType,
): Promise<ProfileIds> => {
  const url = `/api/v1/teams/${teamId}/profiles?type=${profileType}`;
  const { json } = await authenticatedFetch<ProfileIds>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return json();
};

export type ShareType = 'share-digital-profile-link' | 'link' | 'attachment';

export const shareProfiles = async ({
  teamId,
  body,
  shareType,
}: {
  teamId: string;
  body: { profileIds: string[]; profileType?: ProfileType; dialect?: string };
  shareType: ShareType;
}) => {
  const url = `/api/v1/teams/${teamId}/profiles/emails?format=${shareType}`;
  const { json } = await authenticatedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return json();
};

export const downloadTeamProfiles = async ({
  teamId,
  body,
}: {
  teamId: string;
  body: {
    profileIds: string[];
    dialect?: Dialects;
    generateUsing?: ProfileDialectStrategies;
  };
}) => {
  const url = `/api/v1/teams/${teamId}/profiles/email/me`;
  const { json } = await authenticatedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return json();
};

export const getAvailableLearnersForTeam = async (
  teamId: string,
  params?: GetAvailableLearnersRequest,
): Promise<AvailableLearnersResponse> => {
  const queryParams = new URLSearchParams(cleanObject(params ?? {}));
  const url = `/api/v1/teams/${teamId}/available-learners?${queryParams}`;
  const { json } = await authenticatedFetch<AvailableLearnersResponse>(url);
  return json();
};
